import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import PriceDisplayText from '@atoms/PriceDisplayText/PriceDisplayText';
import IKitCarouselCard from '@models/IKitCarouselCard';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';

const KitCarouselCard = ({
  image,
  subTitle,
  productTitle,
  skuLabel,
  vatLabel,
  sku,
  buyNowLink,
  disableFindRetailerCta,
  findRetailerLink,
  rrpLabel,
  pricingData,
  shippingLabel,
  trackingData,
  dataTestId = 'Kit-Carousel-Card',
}: IKitCarouselCard) => {
  const trackingDataJson = JSON.stringify(trackingData ?? '');

  const handleSelectItemEvent = () => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'selectItem',
      ecommerce: {
        currency: trackingData.currency,
        items: [mapToNewTrackingData(trackingData)],
      },
    });
  };

  return (
    <div
      className="grid h-full w-full gap-5 bg-black-100 text-white xl:grid-cols-12 xl:gap-0"
      track-product-impression="true"
      data-product={trackingDataJson}
      data-testid={dataTestId}
    >
      <div className="flex max-h-[400px] justify-center xl:col-span-7 xl:pr-4">
        {image && (
          <a
            href={buyNowLink?.url || '/'}
            onClick={() => {
              if (buyNowLink?.url) handleSelectItemEvent();
            }}
            aria-label={productTitle || ''}
            className="flex h-full w-full justify-center"
            data-testid={`${dataTestId}-Image-Link`}
          >
            <Image
              image={image}
              classes="z-10 h-full"
              imageClasses="h-full object-contain"
              dataTestId={`${dataTestId}-Image`}
            />
          </a>
        )}
      </div>
      <div className="flex flex-col items-center justify-center xl:col-span-4 xl:col-start-9 xl:items-start">
        {subTitle && (
          <span
            className="body-one text-center xl:text-left"
            data-testid={`${dataTestId}-Subtitle`}
          >
            {subTitle}
          </span>
        )}
        {productTitle && (
          <p
            className="h4 mt-5 text-center xl:text-left"
            data-testid={`${dataTestId}-Product-Title`}
          >
            {productTitle}
          </p>
        )}
        {skuLabel && sku && (
          <div className="body-four mt-5 xl:mt-3">
            <span data-testid={`${dataTestId}-Sku-Label`}>{skuLabel}</span>
            <span data-testid={`${dataTestId}-Sku`}>{sku}</span>
          </div>
        )}

        {pricingData?.formattedCalculatedPrice && (
          <div className="mt-5 mb-7.5 xl:mb-4">
            <PriceDisplayText
              pricingData={pricingData}
              vatLabel={vatLabel}
              shippingLabel={shippingLabel}
              rrpLabel={rrpLabel}
              centerText
              containerClassName="xl:block"
              taxesClassName="text-white"
              dataTestId={`${dataTestId}-Price-Display-Text`}
            />
          </div>
        )}

        <div className="mt-auto flex flex-col flex-wrap items-center gap-5 xl:mt-0 xl:flex-row xl:items-start">
          {buyNowLink?.url && buyNowLink?.name && (
            <a
              href={buyNowLink.url}
              onClick={handleSelectItemEvent}
              track-product-click="true"
              data-product={trackingDataJson}
              className="cta primary"
              data-testid={`${dataTestId}-Buy-Now-Link`}
            >
              {buyNowLink.name}
            </a>
          )}

          {!disableFindRetailerCta &&
            findRetailerLink?.url &&
            findRetailerLink?.name && (
              <Button
                link={findRetailerLink}
                secondary
                dataTestId={`${dataTestId}-Find-Retailer-Link`}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default KitCarouselCard;
